import { X } from "lucide-react";

import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MagnifiableImage } from "@/components/waste/magnifiable-image";
import type { BoundingArea } from "@/lib/image-crop";
import { useTranslation } from "@/i18n/use-translation";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string | null;
  beforeImageUrl?: string | null;
  eventId?: string | null;
  /** The capturing device's crop box — both frames render cropped to it (POS2-1105). */
  boundingArea?: BoundingArea | null;
};

/** The lightbox's own chrome around the shared magnifier (POS2-1106). */
function LightboxCapture({
  src,
  alt,
  box,
}: {
  src: string;
  alt: string;
  box: BoundingArea | null;
}) {
  return (
    <MagnifiableImage
      src={src}
      alt={alt}
      box={box}
      frameClassName="rounded-md border border-gray-200"
      className="w-full object-contain transition-transform duration-100 ease-out"
    />
  );
}

export function ImageLightbox({
  open,
  onOpenChange,
  imageUrl,
  beforeImageUrl,
  eventId,
  boundingArea = null,
}: Props) {
  const { t } = useTranslation();
  const hasBoth = Boolean(imageUrl && beforeImageUrl);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90dvh] w-[95vw] max-w-7xl flex-col overflow-hidden sm:max-w-7xl"
      >
        <DialogTitle className="sr-only">{t("waste.photoTitle")}</DialogTitle>
        <div className="flex shrink-0 items-start gap-4">
          {eventId && (
            <p
              className="select-all font-mono text-xs text-gray-400"
              data-testid="lightbox-event-id"
            >
              {t("waste.eventId", { id: eventId })}
            </p>
          )}
          <DialogClose className="ml-auto shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500">
            <X className="h-4 w-4" />
            <span className="sr-only">{t("stock.close")}</span>
          </DialogClose>
        </div>
        <div className="min-h-0 overflow-y-auto">
          <div className={hasBoth ? "grid grid-cols-1 gap-3 md:grid-cols-2" : ""}>
            {beforeImageUrl && (
              <figure>
                <LightboxCapture
                  src={beforeImageUrl}
                  alt={t("waste.beforeAlt")}
                  box={boundingArea}
                />
                {hasBoth && (
                  <figcaption className="mt-1 text-xs text-gray-400">
                    {t("waste.before")}
                  </figcaption>
                )}
              </figure>
            )}
            {imageUrl && (
              <figure>
                <LightboxCapture src={imageUrl} alt={t("waste.afterAlt")} box={boundingArea} />
                {hasBoth && (
                  <figcaption className="mt-1 text-xs text-gray-400">{t("waste.after")}</figcaption>
                )}
              </figure>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
